/** SallaMicroAdapter — slim Salla adapter for v6 thin client. */
import type { MicroAdapter, PageContext, LiveCart } from './MicroAdapter';
import { EMPTY_CART } from './MicroAdapter';

function getSalla(): any {
  if (typeof window === 'undefined') return null;
  return (window as any).salla || (window as any).Salla || null;
}

const SLUG_MAP: Record<string, PageContext['pageType']> = {
  'product.single': 'product',
  'product.index': 'collection',
  'product.index.search': 'search',
  cart: 'cart',
  checkout: 'checkout',
  index: 'home',
  search: 'search',
};

function mapSlug(slug: string): PageContext['pageType'] {
  return SLUG_MAP[slug] ?? 'other';
}

function detectPageFromUrl(url: string, path: string): PageContext {
  const base: Pick<PageContext, 'url' | 'path'> = { url, path };
  const productMatch = path.match(/\/p(\d+)/) || path.match(/-(\d+)$/);
  if (productMatch) return { ...base, pageType: 'product', productId: productMatch[1] };
  if (path.includes('/cart') || path.includes('/basket')) return { ...base, pageType: 'cart' };
  if (path.includes('/checkout')) return { ...base, pageType: 'checkout' };
  // Order confirmation: /thank-you, /order/{id}, /orders/{id}
  const orderMatch = path.match(/\/orders?\/(\d+)/) || path.match(/\/thank-you/);
  if (orderMatch || path.includes('/order-confirmation')) {
    const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
    return { ...base, pageType: 'order_confirmation', orderCode: orderMatch?.[1] || params?.get('id') || params?.get('order_id') || undefined };
  }
  const colMatch = path.match(/\/c(\d+)$/);
  if (colMatch) return { ...base, pageType: 'collection', collectionId: colMatch[1] };
  if (path.includes('/search')) {
    const q = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('q') ?? undefined : undefined;
    return { ...base, pageType: 'search', searchQuery: q };
  }
  if (path === '/' || /^\/(ar|en)\/?$/.test(path)) return { ...base, pageType: 'home' };
  return { ...base, pageType: 'other' };
}

export function createSallaMicroAdapter(): MicroAdapter {
  const cleanups: (() => void)[] = [];

  return {
    detect() {
      return { platform: 'salla', available: !!getSalla() };
    },

    getPageContext(): PageContext {
      const w = typeof window !== 'undefined' ? window : null;
      const url = w?.location?.href ?? '';
      const path = w?.location?.pathname ?? '';
      try {
        const salla = getSalla();
        const page = salla?.config?.get?.('page') as { slug?: string; id?: number | null; title?: string } | undefined;
        if (page?.slug) {
          const pageType = mapSlug(page.slug);
          return {
            url, path, pageType,
            productId: pageType === 'product' && page.id ? String(page.id) : undefined,
            collectionId: pageType === 'collection' && page.id ? String(page.id) : undefined,
            collectionName: pageType === 'collection' && page.title ? page.title : undefined,
          };
        }
      } catch { /* fall through to URL detection */ }
      return detectPageFromUrl(url, path);
    },

    async getCart(): Promise<LiveCart> {
      try {
        const salla = getSalla();
        if (!salla?.cart?.details) return EMPTY_CART;
        const res = await salla.cart.details();
        const d = res?.data;
        const cart = d?.cart ?? d;
        const items = cart?.items ?? [];
        const lastItem = items.length > 0 ? items[items.length - 1] : null;
        return {
          total: cart?.total ?? 0,
          itemCount: cart?.count ?? items.length ?? 0,
          lastItemName: lastItem?.name ?? null,
          currency: cart?.currency ?? 'SAR',
        };
      } catch { return EMPTY_CART; }
    },

    onCartChange(handler) {
      try {
        const salla = getSalla();
        if (!salla?.event?.on) return;
        const cb = async () => { try { handler(await this.getCart()); } catch {} };
        salla.event.on('cart::item.added', cb);
        salla.event.on('cart::item.deleted', cb);
        salla.event.on('cart::item.updated', cb);
      } catch {}
    },

    onCheckoutIntent(handler) {
      if (typeof document === 'undefined') return;
      const listener = (e: Event) => {
        let el = e.target as HTMLElement | null;
        for (let i = 0; el && i < 5; i++, el = el.parentElement) {
          const text = (el.textContent ?? '').toLowerCase();
          const href = (el.getAttribute('href') ?? '').toLowerCase();
          if (text.includes('checkout') || text.includes('\u0625\u062A\u0645\u0627\u0645') || text.includes('\u0627\u062A\u0645\u0627\u0645') || href.includes('checkout')) {
            handler(); return;
          }
        }
      };
      document.addEventListener('click', listener, true);
      cleanups.push(() => document.removeEventListener('click', listener, true));
    },

    onUserLogin(handler) {
      try {
        const salla = getSalla();
        if (!salla?.event?.on) return;
        salla.event.on('auth::verified', () => {
          try {
            const user = salla.config?.get?.('user') as { id?: number; email?: string } | undefined;
            if (user?.id) handler({ id: String(user.id), email: user.email });
          } catch {}
        });
      } catch {}
    },

    async addToCart(productId, quantity = 1) {
      try {
        const salla = getSalla();
        if (!salla?.cart?.addItem) return false;
        await salla.cart.addItem({ id: productId, quantity });
        return true;
      } catch { return false; }
    },

    async applyCoupon(code) {
      try {
        const salla = getSalla();
        if (!salla?.cart?.addCoupon) return false;
        await salla.cart.addCoupon({ coupon: code });
        return true;
      } catch { return false; }
    },

    destroy() {
      cleanups.forEach((fn) => fn());
      cleanups.length = 0;
    },
  };
}
